import { useLoading } from "src/components/hooks/useLoading"
import routes from "routes"
import { getAuth, signInWithEmailAndPassword } from "firebase/auth"
import { useTieupToast } from "src/components/hooks/useTieupToast"
import { useFirebaseAuthError } from "src/components/hooks/useFirebaseAuthError"
import Link from "next/link"
import { Auth as AuthMessage } from "src/config/messages"
import { useForm } from "react-hook-form"
import HTMLHead from "src/components/utils/HTMLHead"
import clsx from "clsx"
import SocialLoginButton from "src/components/common/auth/SocialLoginButton"
import { useLoginConfirm } from "src/components/common/auth/useLoginConfirm"
import InputPassword from "src/components/common/InputPassword"
import React, { useContext, useMemo } from "react"
import { useLocalStorage } from "react-use"
import { DefaultLayoutForReader } from "src/components/common/layouts/DefaultLayoutForReader"
import { usePermission } from "src/components/hooks/usePermission"
import { globalStoreContext } from "src/store/GlobalStore"

type Props = {
  email: string
  password: string
}

export default function SignUp() {
  const { fullScreenLoading } = useLoading()
  const { showFirebaseError } = useFirebaseAuthError()
  const { register, errors, handleSubmit } = useForm<Props>()
  const { addToastInfo } = useTieupToast()
  const { loginConfirm } = useLoginConfirm()

  const {
    globalStore: {
      user: { clientState },
    },
  } = useContext(globalStoreContext)

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [redirectPage, setRedirectPage] = useLocalStorage<string>(
    "redirect_page",
    "",
  )

  const login = async (values: Props) => {
    const { email, password } = values
    const auth = getAuth()

    try {
      // ローディング表示
      fullScreenLoading(true)
      // ログインの実行
      await signInWithEmailAndPassword(auth, email, password)
      addToastInfo("ログインしました")
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error)
      showFirebaseError(error)
    } finally {
      fullScreenLoading(false)
    }
  }

  const onMailLoginSubmit = async (values: Props) => {
    const callLogin = () => login(values)

    loginConfirm(callLogin)
  }

  const redirectUrl = useMemo(() => {
    if (clientState === 1) {
      return routes.authCreatorProfile()
    }

    if (redirectPage) {
      return redirectPage
    }

    return routes.mypage()
  }, [clientState, redirectPage])
  usePermission([0], redirectUrl)

  return (
    <DefaultLayoutForReader>
      <HTMLHead path="auth/sing-in" />
      <main>
        <header className="p-signIn-header">
          <h1 className="p-signIn-header-title">ログイン</h1>
        </header>

        <section className="p-signIn-section">
          <div className="p-signIn-section-inner">
            <h2 className="p-signIn-section-title">メールアドレスでログイン</h2>
            <form onSubmit={handleSubmit(onMailLoginSubmit)}>
              <div className="p-signIn-block">
                <label htmlFor="input-email" className="c-label-primary">
                  登録メールアドレス
                </label>
                <input
                  id="input-email"
                  className={clsx({
                    "c-textfield-primary": true,
                    mailaddress: true,
                    "-error": !!errors.email,
                  })}
                  type="email"
                  name="email"
                  ref={register({ required: true })}
                />
                {errors.email && (
                  <p className="c-note-primary -error">
                    {AuthMessage.error.email}
                  </p>
                )}
              </div>
              <div className="p-signIn-block">
                <label className="c-label-primary">パスワード</label>
                <InputPassword errors={errors} register={register} />
                {errors.password && (
                  <p className="c-note-primary -error">
                    {AuthMessage.error.password}
                  </p>
                )}
              </div>
              <div className="p-button">
                <button
                  className="c-button-secondary login-button -full"
                  type="submit"
                  data-cy="auth-login-button"
                >
                  ログイン
                </button>
              </div>
            </form>
            <p className="p-signIn-link">
              <Link href={routes.authPasswordNew()}>
                <a className="p-signIn-link-anchor">パスワードをお忘れの方</a>
              </Link>
            </p>
          </div>
        </section>
        <section className="p-signIn-section">
          <div className="p-signIn-section-inner">
            <h2 className="p-signIn-section-title">
              ソーシャルアカウントでログイン
            </h2>
            <div className="p-signIn-sns">
              <div className="p-signIn-sns-col">
                <SocialLoginButton providerName="twitter" />
              </div>
              <div className="p-signIn-sns-col">
                <SocialLoginButton providerName="google" />
              </div>
            </div>
          </div>
        </section>
        <section className="p-signIn-section">
          <div className="p-signIn-section-inner">
            <h2 className="p-signIn-section-title">
              アカウントをお持ちでない方
            </h2>
            <div className="p-button">
              <Link href={routes.authSignUp()}>
                <a className="c-button-secondary -full -outline">
                  新規会員登録
                </a>
              </Link>
            </div>
          </div>
        </section>
      </main>
    </DefaultLayoutForReader>
  )
}
